/**
 * VersionHistoryModal.tsx — „Historia wersji" szablonu (Krok 5, read-only).
 *
 * Pokazuje wersje udostępnione klientom (od najnowszej): numer dla klientów, datę,
 * „co nowego" oraz diff względem poprzedniej wersji (zmiany z plakietką bezpieczna/breaking).
 * Czysto prezentacyjny konsument GET /admin/module-templates/:slug/versions.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Modal } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { api } from '@/lib/api';

interface StructureChange {
  kind: string;
  entitySlug: string;
  entityName?: string;
  fieldSlug?: string;
  fieldName?: string;
  detail?: string;
  breaking: boolean;
}

interface VersionEntry {
  clientVersion: string;
  technicalVersion: string;
  publishedAt: string | null;
  releaseNotes: string | null;
  diff: { initial: boolean; hasBreaking: boolean; changes: StructureChange[] } | null;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pl-PL');
}

/** Czytelny opis pojedynczej zmiany (encja › pole + ewentualny szczegół). */
function changeLabel(c: StructureChange, t: (k: string) => string): string {
  const target = c.fieldName
    ? `${c.entityName ?? c.entitySlug} › ${c.fieldName}`
    : (c.entityName ?? c.entitySlug);
  const kind = t(`moduleTemplateEditor.versions.change.${c.kind}`);
  return c.detail ? `${kind}: ${target} (${c.detail})` : `${kind}: ${target}`;
}

export function VersionHistoryModal({
  open,
  onClose,
  templateSlug,
}: {
  open: boolean;
  onClose: () => void;
  templateSlug: string;
}) {
  const { t } = useTranslation();

  const query = useQuery<{ versions: VersionEntry[] }>({
    queryKey: ['admin', 'module-templates', templateSlug, 'versions'],
    queryFn: async () => (await api.get(`/admin/module-templates/${templateSlug}/versions`)).data,
    enabled: open,
    retry: false,
  });

  const versions = useMemo(() => query.data?.versions ?? [], [query.data]);

  return (
    <Modal open={open} onClose={onClose} title={t('moduleTemplateEditor.versions.title')}>
      <div className="flex flex-col gap-4" data-testid="version-history">
        {query.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : query.isError ? (
          <p className="text-sm text-red-600 dark:text-red-400">
            {t('moduleTemplateEditor.versions.loadError')}
          </p>
        ) : versions.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('moduleTemplateEditor.versions.empty')}
          </p>
        ) : (
          versions.map((v) => (
            <section
              key={v.clientVersion}
              className="rounded-lg border border-gray-200 p-3 dark:border-gray-700"
              data-testid={`version-${v.clientVersion}`}
            >
              <div className="mb-1 flex items-center gap-2">
                <Badge color="blue">v{v.clientVersion}</Badge>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(v.publishedAt)} · {v.technicalVersion}
                </span>
                {v.diff?.hasBreaking && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {t('moduleTemplateEditor.versions.breaking')}
                  </span>
                )}
              </div>

              {v.releaseNotes && (
                <p className="mb-2 text-sm text-gray-700 dark:text-gray-300">{v.releaseNotes}</p>
              )}

              {v.diff == null ? null : v.diff.initial ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('moduleTemplateEditor.versions.initial')}
                </p>
              ) : v.diff.changes.length > 0 ? (
                <ul className="flex flex-col gap-1">
                  {v.diff.changes.map((c, i) => (
                    <li
                      key={`${c.entitySlug}-${c.fieldSlug ?? ''}-${i}`}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Badge color={c.breaking ? 'red' : 'green'}>
                        {c.breaking
                          ? t('moduleTemplateEditor.versions.breakingShort')
                          : t('moduleTemplateEditor.versions.safe')}
                      </Badge>
                      <span className="text-gray-700 dark:text-gray-300">{changeLabel(c, t)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('moduleTemplateEditor.versions.noChanges')}
                </p>
              )}
            </section>
          ))
        )}
      </div>
    </Modal>
  );
}
